import { supabase } from '../supabase';
import { analyzeMarketData, getMarketSentiment } from '../openai';

type MarketAnalysis = {
  symbol: string;
  trend: 'bullish' | 'bearish' | 'neutral';
  confidence: number;
  signals: string[];
  prediction: string;
  shortTerm: {
    direction: 'up' | 'down' | 'neutral';
    prediction: string;
    confidence: number;
  };
  midTerm: {
    direction: 'up' | 'down' | 'neutral';
    prediction: string;
    confidence: number;
  };
  longTerm: {
    direction: 'up' | 'down' | 'neutral';
    prediction: string;
    confidence: number;
  };
};

export async function performMarketAnalysis(symbol: string, aiSettings?: any): Promise<MarketAnalysis> {
  try {
    // First check if OpenAI API key is configured
    const { data: settings, error: settingsError } = await supabase
      .from('admin_settings')
      .select('openai_api_key')
      .eq('id', 1)
      .single();

    if (settingsError || !settings?.openai_api_key) {
      console.warn('OpenAI API key not configured, using simulated analysis');
      return generateSimulatedAnalysis(symbol);
    }

    // Get historical market data
    const { data: marketData, error: marketError } = await supabase
      .from('market_data')
      .select('*')
      .eq('symbol', symbol)
      .order('created_at', { ascending: false })
      .limit(24);

    if (marketError) {
      console.warn('Error fetching market data:', marketError);
      return generateSimulatedAnalysis(symbol);
    }

    if (!marketData || marketData.length === 0) {
      console.warn('No market data available for', symbol);
      return generateSimulatedAnalysis(symbol);
    }

    try {
      // Get AI analysis with settings
      const analysis = await analyzeMarketData(symbol, marketData, aiSettings);
      
      // Get market sentiment
      const sentiment = await getMarketSentiment(symbol);

      // Combine analysis with sentiment
      const result: MarketAnalysis = {
        symbol,
        trend: analysis.trend,
        confidence: analysis.confidence,
        signals: [...analysis.signals, `Market Sentiment: ${sentiment}`],
        prediction: analysis.prediction,
        shortTerm: analysis.shortTerm,
        midTerm: analysis.midTerm,
        longTerm: analysis.longTerm
      };

      // Store analysis for historical tracking
      await storeAnalysis(result);
      
      return result;
    } catch (error) {
      console.warn('AI analysis failed, using simulated analysis:', error);
      return generateSimulatedAnalysis(symbol);
    }
  } catch (error) {
    console.warn('Falling back to simulated analysis:', error);
    return generateSimulatedAnalysis(symbol);
  }
}

async function storeAnalysis(analysis: MarketAnalysis) {
  try {
    const { error } = await supabase
      .from('market_analysis')
      .insert([{
        symbol: analysis.symbol,
        trend: analysis.trend,
        confidence: analysis.confidence,
        signals: analysis.signals,
        prediction: analysis.prediction,
        created_at: new Date().toISOString()
      }]);

    if (error) {
      console.warn('Failed to store analysis:', error);
    }
  } catch (error) {
    console.warn('Error storing analysis:', error);
  }
}

function generateSimulatedAnalysis(symbol: string): MarketAnalysis {
  const trends: Array<'bullish' | 'bearish' | 'neutral'> = ['bullish', 'bearish', 'neutral'];
  const trend = trends[Math.floor(Math.random() * trends.length)];
  
  const mockSignals = {
    bullish: [
      'Technical indicators suggest upward momentum',
      'Volume analysis shows accumulation',
      'Price action forming bullish pattern',
      'Market sentiment trending positive'
    ],
    bearish: [
      'Technical indicators show weakness',
      'Volume analysis indicates distribution',
      'Price action forming bearish pattern',
      'Market sentiment trending negative'
    ],
    neutral: [
      'Mixed technical signals',
      'Balanced volume profile',
      'Sideways price action',
      'Neutral market sentiment'
    ]
  };

  const signals = mockSignals[trend];
  const confidence = 50 + Math.floor(Math.random() * 30); // More conservative confidence

  return {
    symbol,
    trend,
    confidence,
    signals,
    prediction: generatePrediction(symbol, trend, confidence),
    shortTerm: {
      direction: trend === 'bullish' ? 'up' : trend === 'bearish' ? 'down' : 'neutral',
      prediction: `Short-term outlook shows ${trend} bias`,
      confidence: confidence
    },
    midTerm: {
      direction: trend === 'bullish' ? 'up' : trend === 'bearish' ? 'down' : 'neutral',
      prediction: `Mid-term analysis indicates ${trend} momentum`,
      confidence: Math.max(confidence - 10, 0)
    },
    longTerm: {
      direction: trend === 'bullish' ? 'up' : trend === 'bearish' ? 'down' : 'neutral',
      prediction: `Long-term trend suggests ${trend} market structure`,
      confidence: Math.max(confidence - 20, 0)
    }
  };
}

function generatePrediction(symbol: string, trend: string, confidence: number): string {
  const strengthLevel = confidence >= 70 ? 'strong' : confidence >= 50 ? 'moderate' : 'weak';
  const riskLevel = confidence >= 70 ? 'high' : confidence >= 50 ? 'moderate' : 'low';

  switch (trend) {
    case 'bullish':
      return `${symbol} shows ${strengthLevel} bullish momentum with ${riskLevel} risk level. Technical analysis suggests potential upward movement. Consider appropriate risk management.`;
    case 'bearish':
      return `${symbol} indicates ${strengthLevel} bearish pressure with ${riskLevel} risk level. Multiple indicators suggest downward bias. Implement proper risk controls.`;
    default:
      return `${symbol} displays ${strengthLevel} consolidation with ${riskLevel} risk level. Market conditions suggest range-bound trading. Wait for clearer signals.`;
  }
}
